import { useState, useCallback, useEffect } from 'react';
import { FiPlusCircle, FiMinusCircle, FiLoader } from 'react-icons/fi';

/**
 * VideoTreeSidebar
 *
 * Props:
 *  sections         – array of { key, label, renderKey }
 *  filterTree       – { years: [], themes: [] } from the section filter tree
 *                     years[].months[].weeks[].videos[]
 *  onFilterSelect   – callback(filters) when user picks a year/month/week/day
 *                     filter shape: { annee, mois, dateFrom, dateTo }
 *  onFetchThemeData – async (theme: string) => { years: [], videoCount: number }
 *                     called lazily the first time a theme is expanded
 *  onClearFilters   – callback when the clear button is clicked
 *  filters          – current active filters object
 *  selectedVideo    – current selected/previewed video object
 *  className        – extra CSS class for the <aside>
 */
export default function VideoTreeSidebar({
  sections: extraSections = [],
  filterTree = {},
  onFilterSelect,
  onFetchThemeData,
  onClearFilters,
  filters = {},
  selectedVideo = null,
  multipleVideosPerDay = false,
  isDayBased = false,
  className = '',
}) {
  const [openSection, setOpenSection] = useState(null);
  const [openYears, setOpenYears] = useState({});
  const [openMonths, setOpenMonths] = useState({});
  const [openWeeks, setOpenWeeks] = useState({});
  const [openDays, setOpenDays] = useState({});
  const [openThemes, setOpenThemes] = useState({});
  // per-theme year/month trees fetched on demand
  const [themeData, setThemeData] = useState({});
  const [themeLoading, setThemeLoading] = useState({});
  // per-theme year/month/day open state (keyed with theme prefix to avoid collision)
  const [openThemeYears, setOpenThemeYears] = useState({});
  const [openThemeMonths, setOpenThemeMonths] = useState({});
  const [openThemeDays, setOpenThemeDays] = useState({});

  // True once the user has clicked any Filtrer link or video in the tree
  const [hasTreeSelection, setHasTreeSelection] = useState(false);

  // Collapse all tree nodes and reset selection flag
  const collapseAll = useCallback(() => {
    setOpenYears({});
    setOpenMonths({});
    setOpenWeeks({});
    setOpenDays({});
    setOpenThemes({});
    setOpenThemeYears({});
    setOpenThemeMonths({});
    setOpenThemeDays({});
    setHasTreeSelection(false);
  }, []);

  // When the parent clears its filters externally, also collapse the tree
  // Use primitive values as deps — object refs change every render and cause infinite loops
  const filtersAnnee = filters.annee;
  const filtersMois = filters.mois;
  const filtersTheme = filters.theme;
  const selectedVideoId = selectedVideo?.id ?? selectedVideo?._id ?? null;
  useEffect(() => {
    const hasActive = filtersAnnee || filtersMois || filtersTheme || selectedVideoId;
    if (!hasActive) {
      collapseAll();
    }
  }, [filtersAnnee, filtersMois, filtersTheme, selectedVideoId, collapseAll]);

  const toggleSection = (key) => setOpenSection(prev => prev === key ? null : key);
  const toggle = (setObj, key) => setObj(prev => ({ ...prev, [key]: !prev[key] }));

  const handleFilterClick = (newFilters) => {
    setHasTreeSelection(true);
    if (onFilterSelect) onFilterSelect(newFilters);
  };

  // Format week label from "DD/MM/YYYY" format to "Du DD au DD month"
  const formatWeekLabel = (from, to, monthName) => {
    if (!from || !to) return '';
    
    // Extract day numbers from DD/MM/YYYY format
    const fromDay = from.split('/')[0];
    const toDay = to.split('/')[0];
    
    return `Du ${fromDay} au ${toDay} ${monthName.toLowerCase()}`;
  };

  // Format individual date like "Lundi 24 décembre 2022"
  const formatFullDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${dayName} ${day} ${month} ${year}`;
  };

  // Extract unique days from week's videos, grouped with video list
  const extractDaysFromWeek = (week) => {
    if (!week.videos || week.videos.length === 0) return [];
    
    const dayMap = {};
    week.videos.forEach(video => {
      if (video.date) {
        const dateObj = new Date(video.date);
        const dateStr = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
        if (!dayMap[dateStr]) {
          dayMap[dateStr] = {
            date: video.date,
            dateStr,
            dayLabel: formatFullDate(video.date),
            videos: [],
          };
        }
        dayMap[dateStr].videos.push(video);
      }
    });
    // Sort by date and return as array
    return Object.values(dayMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const handleClear = () => {
    collapseAll();
    if (onClearFilters) onClearFilters();
  };

  /* ── check if selectedVideo lives anywhere under a theme's loaded data ── */
  const isVideoUnderTheme = (theme) => {
    if (!selectedVideo) return false;
    const data = themeData[theme];
    if (!data?.years) return false;
    return data.years.some(y =>
      y.months?.some(m =>
        m.weeks?.some(w =>
          w.videos?.some(v =>
            (selectedVideo._id && v._id && selectedVideo._id === v._id) ||
            (selectedVideo.id && v._id && selectedVideo.id === v._id) ||
            (selectedVideo.id && v.id && selectedVideo.id === v.id)
          )
        ) ||
        m.days?.some(d =>
          d.videos?.some(v =>
            (selectedVideo._id && v._id && selectedVideo._id === v._id) ||
            (selectedVideo.id && v._id && selectedVideo.id === v._id) ||
            (selectedVideo.id && v.id && selectedVideo.id === v.id)
          )
        )
      )
    );
  };

  /* ── expand/collapse a theme, fetching its tree the first time ── */
  const handleThemeToggle = useCallback(async (theme) => {
    const willOpen = !openThemes[theme];
    setOpenThemes(prev => ({ ...prev, [theme]: willOpen }));

    if (willOpen && !themeData[theme] && onFetchThemeData) {
      setThemeLoading(prev => ({ ...prev, [theme]: true }));
      try {
        const data = await onFetchThemeData(theme);
        setThemeData(prev => ({ ...prev, [theme]: data }));
      } catch (err) {
        console.error('Failed to load theme tree', err);
      } finally {
        setThemeLoading(prev => ({ ...prev, [theme]: false }));
      }
    }
  }, [openThemes, themeData, onFetchThemeData]);

  /* ── shared month renderer (used for both byYear and byTheme trees) ── */
  const renderMonths = (months, yearData, prefix = '') =>
    months.map((m) => {
      const mKey = `${prefix}${yearData.year}-${m.month}`;
      const isOpen = !!openThemeMonths[mKey] || !!openMonths[mKey];
      const setMonthFn = prefix ? setOpenThemeMonths : setOpenMonths;
      const setWeekFn = prefix ? setOpenThemeMonths : setOpenWeeks;
      const setDayFn = prefix ? setOpenThemeDays : setOpenDays;
      const weekOpenState = prefix ? openThemeMonths : openWeeks;
      const dayOpenState = prefix ? openThemeDays : openDays;
      const themeName = prefix ? prefix.split('::')[0] : '';
      const isSelectedMonth = String(filters.annee) === String(yearData.year) &&
        String(filters.mois) === String(m.month) &&
        String(filters.theme || '') === String(themeName || '') &&
        !filters.dateFrom && !filters.dateTo;
      const weekEntries = Array.isArray(m.weeks) ? m.weeks : [];
      const dayEntries = Array.isArray(m.days) ? m.days : [];
      
      // For day-based structures (motivation_lundi), skip weeks and render days directly
      const shouldSkipWeeks = isDayBased && dayEntries.length > 0;
      
      return (
        <div key={m.month} style={{ marginBottom: '6px' }}>
          <div
            className={`tree-month${isOpen ? ' open' : ''}${isSelectedMonth ? ' selected' : ''}`}
            style={{ 
              paddingLeft: 20, 
              cursor: 'pointer', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '6px 8px 6px 20px', 
              lineHeight: 1.3
            }}>
            <div onClick={() => toggle(setMonthFn, mKey)} style={{ flex: 1 }}>
              <span className="icon" style={{ marginRight: 6 }}>{isOpen ? '-' : '+'}</span>
              {m.monthName}
            </div>
          </div>

          {isOpen && (
            <div>
              {/* If day-based structure, render days directly without weeks */}
              {shouldSkipWeeks && dayEntries.length > 0 && dayEntries.map((d) => {
                const dKey = `${mKey}-${d.day}`;
                const isDayOpen = !!dayOpenState[dKey];
                const isDaySelected = d.videos?.some(v =>
                  selectedVideo && (
                    (selectedVideo._id && v._id && selectedVideo._id === v._id) ||
                    (selectedVideo.id && v._id && selectedVideo.id === v._id) ||
                    (selectedVideo.id && v.id && selectedVideo.id === v.id)
                  )
                );

                return (
                  <div key={dKey}>
                    <div
                      className={`tree-day${isDaySelected ? ' selected' : ''}`}
                      style={{ 
                        paddingLeft: 32, 
                        cursor: 'pointer', 
                        display: 'flex',
                        justifyContent: multipleVideosPerDay ? 'flex-start' : 'space-between',
                        gap: multipleVideosPerDay ? '6px' : undefined,
                        alignItems: 'center',
                        padding: '5px 8px 5px 32px', 
                        lineHeight: 1.3, 
                        fontSize: '12px',
                        color: '#666',
                        fontWeight: isDaySelected ? '700' : '400'
                      }}
                      onClick={() => {
                        if (multipleVideosPerDay) {
                          toggle(setDayFn, dKey);
                          return;
                        }
                        if (d.videos && d.videos.length > 0) {
                          const firstVideo = d.videos[0];
                          handleFilterClick({
                            annee: yearData.year,
                            mois: m.month,
                            jour: d.day,
                            dateFrom: '',
                            dateTo: '',
                            _videoId: firstVideo._id || firstVideo.id,
                            _video: firstVideo,
                            ...(themeName && { theme: themeName }),
                          });
                        } else {
                          handleFilterClick({
                            annee: yearData.year,
                            mois: m.month,
                            jour: d.day,
                            dateFrom: '',
                            dateTo: '',
                            ...(themeName && { theme: themeName }),
                          });
                        }
                      }}
                      title={d.dayLabel}
                    >
                      {multipleVideosPerDay && <span className="icon" style={{ marginRight: 6 }}>{isDayOpen ? '-' : '+'}</span>}
                      <span>{d.dayLabel || d.day}</span>
                      {!multipleVideosPerDay && isDaySelected && (
                        <span style={{ marginLeft: '8px', flexShrink: 0, fontSize: '16px', color: '#666' }}>✓</span>
                      )}
                    </div>
                    {multipleVideosPerDay && isDayOpen && d.videos?.length > 0 && (
                      <div>
                        {d.videos.map((video) => {
                          const isVideoSelected = selectedVideo && (
                            (selectedVideo._id && video._id && selectedVideo._id === video._id) ||
                            (selectedVideo.id && video._id && selectedVideo.id === video._id) ||
                            (selectedVideo.id && video.id && selectedVideo.id === video.id)
                          );
                          return (
                            <div
                              key={video._id || video.id}
                              className={`tree-day${isVideoSelected ? ' selected' : ''}`}
                              style={{ padding: '5px 8px 5px 44px', cursor: 'pointer', fontSize: '12px', lineHeight: 1.3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                              onClick={() => handleFilterClick({
                                annee: yearData.year,
                                mois: m.month,
                                jour: d.day,
                                dateFrom: '',
                                dateTo: '',
                                _videoId: video._id || video.id,
                                _video: video,
                                ...(themeName && { theme: themeName }),
                              })}
                              title={video.lien_primaire || video.theme || 'Vidéo'}>
                              <span>{video.theme || video.titre || video.title || `Vidéo ${d.videos.indexOf(video) + 1}`}</span>
                              {isVideoSelected && <span style={{ marginLeft: '8px', flexShrink: 0, fontSize: '16px', color: '#666' }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* If week-based structure, render weeks then days */}
              {!shouldSkipWeeks && weekEntries.length > 0 && weekEntries.map((w) => {
                const wKey = `${mKey}-week-${w.week || w.from || w.to}`;
                const isWeekOpen = !!weekOpenState[wKey];
                const days = extractDaysFromWeek(w);

                return (
                  <div key={wKey}>
                    <div
                      className={`tree-week${isWeekOpen ? ' open' : ''}`}
                      style={{ 
                        paddingLeft: 32, 
                        cursor: 'pointer', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '5px 8px 5px 32px', 
                        lineHeight: 1.3, 
                        fontSize: '13px',
                      }}>
                      <div onClick={() => toggle(setWeekFn, wKey)} style={{ flex: 1 }}>
                        <span className="icon" style={{ marginRight: 6 }}>{isWeekOpen ? '-' : '+'}</span>
                        {formatWeekLabel(w.from, w.to, m.monthName) || w.weekLabel || `Semaine ${w.week || ''}`}
                      </div>
                    </div>

                    {/* Show individual days when week is expanded */}
                    {isWeekOpen && days.length > 0 && (
                      <div>
                        {days.map((day) => {
                          const dKey = `${wKey}-${day.dateStr}`;
                          const isDayOpen = !!dayOpenState[dKey];
                          const isDaySelected = day.videos?.some(v =>
                            selectedVideo && (
                              (selectedVideo._id && v._id && selectedVideo._id === v._id) ||
                              (selectedVideo.id && v._id && selectedVideo.id === v._id) ||
                              (selectedVideo.id && v.id && selectedVideo.id === v.id)
                            )
                          );

                          return (
                            <div key={dKey}>
                              <div
                                className={`tree-day${isDaySelected ? ' selected' : ''}`}
                                style={{ 
                                  paddingLeft: 44, 
                                  cursor: 'pointer', 
                                  display: 'flex',
                                  justifyContent: multipleVideosPerDay ? 'flex-start' : 'space-between',
                                  gap: multipleVideosPerDay ? '6px' : undefined,
                                  alignItems: 'center',
                                  padding: '5px 8px 5px 44px', 
                                  lineHeight: 1.3, 
                                  fontSize: '12px',
                                  color: '#666',
                                  fontWeight: isDaySelected ? '700' : '400'
                                }}
                                onClick={() => {
                                  if (multipleVideosPerDay) {
                                    toggle(setDayFn, dKey);
                                    return;
                                  }
                                  if (day.videos && day.videos.length > 0) {
                                    const firstVideo = day.videos[0];
                                    handleFilterClick({
                                      annee: yearData.year,
                                      mois: m.month,
                                      jour: day.dateStr,
                                      dateFrom: w.from,
                                      dateTo: w.to,
                                      _videoId: firstVideo._id || firstVideo.id,
                                      _video: firstVideo,
                                      ...(themeName && { theme: themeName }),
                                    });
                                  } else {
                                    handleFilterClick({
                                      annee: yearData.year,
                                      mois: m.month,
                                      jour: day.dateStr,
                                      dateFrom: w.from,
                                      dateTo: w.to,
                                      ...(themeName && { theme: themeName }),
                                    });
                                  }
                                }}
                                title={day.dayLabel}
                              >
                                {multipleVideosPerDay && <span className="icon" style={{ marginRight: 6 }}>{isDayOpen ? '-' : '+'}</span>}
                                <span>{day.dayLabel}</span>
                                {!multipleVideosPerDay && isDaySelected && (
                                  <span style={{ marginLeft: '8px', flexShrink: 0, fontSize: '16px', color: '#666' }}>✓</span>
                                )}
                              </div>
                              {multipleVideosPerDay && isDayOpen && day.videos?.length > 0 && (
                                <div>
                                  {day.videos.map((video) => {
                                    const isVideoSelected = selectedVideo && (
                                      (selectedVideo._id && video._id && selectedVideo._id === video._id) ||
                                      (selectedVideo.id && video._id && selectedVideo.id === video._id) ||
                                      (selectedVideo.id && video.id && selectedVideo.id === video.id)
                                    );
                                    return (
                                      <div
                                        key={video._id || video.id}
                                        className={`tree-day${isVideoSelected ? ' selected' : ''}`}
                                        style={{ padding: '5px 8px 5px 60px', cursor: 'pointer', fontSize: '12px', lineHeight: 1.3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        onClick={() => handleFilterClick({
                                          annee: yearData.year,
                                          mois: m.month,
                                          jour: day.dateStr,
                                          dateFrom: w.from,
                                          dateTo: w.to,
                                          _videoId: video._id || video.id,
                                          _video: video,
                                          ...(themeName && { theme: themeName }),
                                        })}
                                        title={video.lien_primaire || video.theme || 'Vidéo'}
                                      >
                                        <span>{video.theme || video.titre || video.title || `Vidéo ${day.videos.indexOf(video) + 1}`}</span>
                                        {isVideoSelected && <span style={{ marginLeft: '8px', flexShrink: 0, fontSize: '16px', color: '#666' }}>✓</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Keep existing day-level rendering for compatibility */}
              {!shouldSkipWeeks && dayEntries.length > 0 && dayEntries.map((d) => {
                const dKey = `${mKey}-${d.day}`;
                const isDayOpen = !!dayOpenState[dKey];
                return (
                  <div key={d.day}>
                    <div
                      className={`tree-week${isDayOpen ? ' open' : ''}`}
                      style={{ 
                        paddingLeft: 32, 
                        cursor: 'pointer', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '5px 8px 5px 32px', 
                        lineHeight: 1.3, 
                        fontSize: '13px'
                      }}>
                      <div onClick={() => toggle(setDayFn, dKey)} style={{ flex: 1 }}>
                        <span className="icon" style={{ marginRight: 6 }}>{isDayOpen ? '-' : '+'}</span>
                        {d.dayLabel || d.day}
                      </div>
                    </div>
                    {isDayOpen && d.videos && d.videos.length > 0 && (
                      <div>
                        {d.videos.map((v) => {
                          const isSelectedVideo = selectedVideo && (
                            (selectedVideo._id && v._id && selectedVideo._id === v._id) ||
                            (selectedVideo.id && v._id && selectedVideo.id === v._id) ||
                            (selectedVideo.id && v.id && selectedVideo.id === v.id)
                          );
                          return (
                            <div
                              key={v._id}
                              className={`tree-day ${isSelectedVideo ? ' selected' : ''}`}
                              style={{ 
                                paddingLeft: 48, 
                                cursor: 'pointer', 
                                fontSize: '12px', 
                                lineHeight: 1.3, 
                                padding: '5px 8px 5px 48px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                              onClick={() => handleFilterClick({
                                annee: yearData.year,
                                mois: m.month,
                                jour: d.day,
                                _videoId: v._id,
                                _video: v,
                                ...(themeName && { theme: themeName }),
                                dateFrom: '',
                                dateTo: '',
                              })}
                              title={v.lien_primaire}
                            >
                              <span>{v.theme || 'Vidéo'}</span>
                              {isSelectedVideo && (
                                <span style={{ marginLeft: '8px', flexShrink: 0, fontSize: '16px', color: '#666' }}>✓</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });

  /* ── year tree for the main "byYear" section ── */
  const renderYears = () => {
    const years = filterTree.years || [];
    return years.map(yData => {
      const year = yData.year;
      const isOpen = !!openYears[year];
      const isSelectedYear =
        String(filters.annee) === String(year) && !filters.mois && !filters.theme;

      return (
        <div key={year} style={{ marginBottom: '8px' }}>
          <div
            className={`tree-year${isOpen ? ' open' : ''}${isSelectedYear ? ' selected' : ''}`}
            style={{ 
              paddingLeft: 8, 
              cursor: 'pointer', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '6px 8px', 
              lineHeight: 1.3, 
              fontWeight: '500'
            }}
          >
            <div onClick={() => toggle(setOpenYears, year)} style={{ flex: 1 }}>
              <span className="icon" style={{ marginRight: 6 }}>{isOpen ? '-' : '+'}</span>
              {year}
            </div>
            {/* <span
              style={{ fontSize: '11px', color: '#888', padding: '2px 6px', background: '#f0f0f0', borderRadius: '4px' }}
              onClick={(e) => { e.stopPropagation(); handleFilterClick({ annee: year, mois: '' }); }}
            >
              Filtrer
            </span> */}
          </div>
          {isOpen && yData.months && renderMonths(yData.months, yData)}
        </div>
      );
    });
  };

  /* ── year tree nested under a theme ── */
  const renderThemeYears = (years, theme) =>
    years.map(yData => {
      const yKey = `${theme}::${yData.year}`;
      const isOpen = !!openThemeYears[yKey];
      const isSelectedYear =
        String(filters.annee) === String(yData.year) &&
        !filters.mois &&
        String(filters.theme) === String(theme);

      return (
        <div key={yData.year} style={{ marginBottom: '3px' }}>
          <div
            className={`tree-year${isOpen ? ' open' : ''}${isSelectedYear ? ' selected' : ''}`}
            style={{ paddingLeft: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px 4px 16px', lineHeight: 1.3 }}
          >
            <div onClick={() => toggle(setOpenThemeYears, yKey)} style={{ flex: 1 }}>
              <span className="icon" style={{ marginRight: 6 }}>{isOpen ? '-' : '+'}</span>
              {yData.year}
            </div>
            {/* <span
              style={{ fontSize: '11px', color: '#888', padding: '2px 6px', background: '#f0f0f0', borderRadius: '4px' }}
              onClick={(e) => { e.stopPropagation(); handleFilterClick({ theme, annee: yData.year, mois: '' }); }}
            >
              Filtrer
            </span> */}
          </div>
          {isOpen && yData.months && renderMonths(yData.months, yData, `${theme}::`)}
        </div>
      );
    });

  /* ── theme list, each expandable with lazy-loaded year/month/video tree ── */
  const renderThemes = () => {
    const themes = filterTree.themes || [];
    return themes.map(theme => {
      const isOpen = !!openThemes[theme];
      const isLoading = !!themeLoading[theme];
      const data = themeData[theme];
      const isActiveTheme = isVideoUnderTheme(theme);

      return (
        <div key={theme} style={{ marginBottom: '4px' }}>
          <div
            className={`tree-year${isOpen ? ' open' : ''}${isActiveTheme ? ' selected' : ''}`}
            style={{ paddingLeft: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', lineHeight: 1.3, fontWeight: '500' }}
          >
            <div onClick={() => handleThemeToggle(theme)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span className="icon" style={{ marginRight: 6 }}>{isOpen ? '-' : '+'}</span>
              <span style={{ flex: 1 }}>{theme}</span>
              {isLoading && <FiLoader size={11} style={{ color: '#aaa', animation: 'spin 1s linear infinite' }} />}
              {isActiveTheme && !isOpen && <span style={{ fontSize: '14px', color: '#666', flexShrink: 0 }}>✓</span>}
            </div>
            {/* <span
              style={{ fontSize: '11px', color: '#888', padding: '2px 6px', background: '#f0f0f0', borderRadius: '4px', flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); handleFilterClick({ theme, annee: '', mois: '' }); }}>
              Filtrer
            </span> */}
          </div>

          {isOpen && (
            <div style={{ marginTop: '2px' }}>
              {isLoading ? (
                <div style={{ padding: '4px 8px 4px 28px', fontSize: 12, color: '#aaa', lineHeight: 1.3 }}>
                  Chargement…
                </div>
              ) : data && data.years && data.years.length > 0 ? (
                renderThemeYears(data.years, theme)
              ) : data ? (
                <div style={{ padding: '4px 8px 4px 28px', fontSize: 12, color: '#aaa', lineHeight: 1.3 }}>
                  Aucune vidéo disponible.
                </div>
              ) : null}
            </div>
          )}
        </div>
      );
    });
  };

  const renderMap = { byYear: renderYears, byTheme: renderThemes };

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <aside className={`sidebar-col ${className}`}>

        {/* Clear button — only visible after the user has clicked a Filtrer link or video */}
        {hasTreeSelection && (
          <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #eee', marginBottom: '8px' }}>
            <button
              onClick={handleClear}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: 'var(--mcm-light)',
                color: 'var(--mcm)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.15s ease',
              }}
            >
              Tout afficher / Réinitialiser
            </button>
          </div>
        )}

        {extraSections.map(sec => (
          <div key={sec.key} className="sidebar-section">
            <button
              className={`sidebar-section-btn${openSection === sec.key ? ' open' : ''}`}
              onClick={() => toggleSection(sec.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                width: '100%',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--mcm, #8B0000)',
                textAlign: 'left',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                border: '1.5px solid currentColor',
                borderRadius: '50%',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {openSection === sec.key ? '−' : '+'}
              </span>
              {sec.label}
            </button>
            {openSection === sec.key && (
              <div className="sidebar-tree" style={{ padding: '8px 0 8px 12px' }}>
                {(!filterTree.years || filterTree.years.length === 0) && (!filterTree.themes || filterTree.themes.length === 0)
                  ? <div style={{ padding: '8px 12px', color: '#aaa', fontSize: 13 }}>Aucune vidéo disponible.</div>
                  : renderMap[sec.renderKey]?.()
                }
              </div>
            )}
          </div>
        ))}
      </aside>
    </>
  );
}
